import React, {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from "react";
import { useDispatch, useSelector } from "react-redux";
import { updateApplications } from "../../../../state/slices/application";
import { useUpdateApplicationMutation } from "../../../../state/services/applications";

const KEY_ENTER = 13;

export const formatDate = (dateString) => {
  const options = {
    year: "numeric",
    month: "short",
    day: "numeric",
    hour: "numeric",
    minute: "numeric",
  };
  return new Date(dateString).toLocaleDateString(undefined, options);
};

// This component will be rendered when a note/comment cell is double clicked on so the user can edit
// the text. It is a box larger than the cell default size. It renders markdown! It appends the new
// text to the existing one. It prepends the new text with the name of the user (and date/time).
// It also takes care of saving the edits by dispatching actions (redux store).
const NoteCellEditor = forwardRef((props, ref) => {
  const { rows, cols, maxLength } = props;
  const [row] = useState(props.data);
  const [col] = useState(props.column.colId);
  const [initVal] = useState(props.value ? props.value : "");
  const [value, setValue] = useState("");
  const refInput = useRef(null);
  const dispatch = useDispatch();
  const userInfo = useSelector((state) => state.auth.userInfo);
  const [alreadyStartedTyping, setAlreadyStartedTyping] = useState(false);
  const [stamp, setStamp] = useState("");

  const [updateApplication] = useUpdateApplicationMutation();

  const onChange = (event) => {
    setValue(event.target.value);
  };

  const onKeyDown = (event) => {
    // Get the code of pressed key
    const keyCode = event.which || event.keyCode;
    if (keyCode === KEY_ENTER && event.shiftKey) {
      // Pressed shift + enter, don't submit, instead add a new line!
      event.preventDefault(); // don't submit
      event.stopPropagation();
      setValue((val) => val + "\n"); // instead add a new line!
    }
  };

  useEffect(() => {
    // focus on the input
    setTimeout(() => refInput.current.focus());

    if (!alreadyStartedTyping) {
      const time_stamp = formatDate(Date.now());
      const newLine = initVal.length === 0 ? "" : "\n\n---\n\n";
      setAlreadyStartedTyping(true);
      const text = `${initVal}${newLine}**${userInfo.firstName} ${userInfo.lastName}** \`(${time_stamp})\`: `;
      setStamp(text);
      setValue(text);
    }
  }, []);

  /* Component Editor Lifecycle methods */
  useImperativeHandle(ref, () => {
    return {
      // the final value to send to the grid, on completion of editing
      getValue() {
        return value;
      },

      // Gets called once before editing starts, to give editor a chance to
      // cancel the editing before it even starts.
      isCancelBeforeStart() {
        return false;
      },

      // Gets called once when editing is finished (eg if Enter is pressed).
      // If you return true, then the result of the edit will be ignored.
      isCancelAfterEnd() {
        if (stamp.trim() !== value.trim()) {
          const newData = { ...row, [col]: value };
          updateApplication(newData);
          dispatch(updateApplications(newData));
        }
        return true; // stop Ag-Grid from modifying the immutable data!
      },

      isPopup() {
        return true;
      },
    };
  });

  return (
    <div style={{ padding: "10px" }}>
      <textarea
        ref={refInput}
        value={value}
        onChange={onChange}
        cols={cols}
        rows={rows}
        // maxLength={maxlength}
        onKeyDown={onKeyDown}
      />
      <div>
        <b>Markdown Enabled!</b> To save, press <code>Enter</code>. For new
        line, press <code>Shift</code> + <code>Enter</code>.
      </div>
    </div>
  );
});

export default NoteCellEditor;
